import { useEffect, useId, useState } from 'react';
import { Button } from '../components/Button/Button';
import { useReportGalleryEntry } from './useGallery';
import type { ReportReason } from './galleryApi';
import './ReportDialog.css';

const REASONS: Array<{ value: ReportReason; label: string }> = [
  { value: 'inappropriate-content', label: 'Inappropriate content' },
  { value: 'personal-info', label: 'Shows personal info' },
  { value: 'harassment', label: 'Harassment or bullying' },
  { value: 'copyright', label: 'Uses copyrighted work' },
  { value: 'spam', label: 'Spam' },
  { value: 'other', label: 'Something else' },
];

interface Props {
  isOpen: boolean;
  submissionId: string;
  projectName: string;
  onClose: () => void;
}

/**
 * Public report flow. Anyone — signed in or not — can flag a gallery
 * submission for moderator review. Reasons are a controlled
 * vocabulary; the optional detail textarea lets the reporter say
 * more in their own words.
 *
 * Privacy: when the reporter happens to be signed in, the backend
 * stamps `reporter_id` on the row so abuse-of-report patterns are
 * easier to spot. The kid is told this — the lead copy mentions
 * "a grown-up at Bryta" reviews it.
 */
export function ReportDialog({
  isOpen,
  submissionId,
  projectName,
  onClose,
}: Props): JSX.Element | null {
  const titleId = useId();
  const reportMut = useReportGalleryEntry();
  const [reason, setReason] = useState<ReportReason | ''>('');
  const [detail, setDetail] = useState('');
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setReason('');
      setDetail('');
      setSent(false);
      reportMut.reset();
      return;
    }
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!reason) return;
    reportMut.mutate(
      {
        id: submissionId,
        reason,
        ...(detail.trim() ? { detail: detail.trim() } : {}),
      },
      { onSuccess: () => setSent(true) },
    );
  };

  return (
    <div
      className="bl-report__backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !reportMut.isPending) onClose();
      }}
    >
      <div className="bl-report__dialog" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 id={titleId} className="bl-report__title">
          Report &ldquo;{projectName}&rdquo;
        </h2>

        {sent ? (
          <>
            <p className="bl-report__lead">
              Thanks. A grown-up at BytaApps will take a look. You can close this window.
            </p>
            <div className="bl-report__actions">
              <Button onClick={onClose}>Close</Button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit} className="bl-report__form">
            <p className="bl-report__lead">
              If something feels wrong about this project, let us know. A grown-up at BytaApps
              reviews every report.
            </p>
            <fieldset className="bl-report__reasons">
              <legend>Why are you reporting it?</legend>
              {REASONS.map((r) => (
                <label key={r.value} className="bl-report__reason">
                  <input
                    type="radio"
                    name="reason"
                    value={r.value}
                    checked={reason === r.value}
                    onChange={() => setReason(r.value)}
                  />
                  <span>{r.label}</span>
                </label>
              ))}
            </fieldset>
            <label className="bl-report__detail">
              <span>Anything else? (optional)</span>
              <textarea
                rows={3}
                maxLength={2000}
                value={detail}
                onChange={(e) => setDetail(e.target.value)}
                placeholder="Short is fine — be honest."
              />
            </label>
            {reportMut.error && (
              <p role="alert" className="bl-report__error">
                {reportMut.error.message}
              </p>
            )}
            <div className="bl-report__actions">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={reportMut.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!reason || reportMut.isPending}>
                {reportMut.isPending ? 'Sending…' : 'Send report'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
